import { formatarAteDuasCasas, formatarDataHora, formatarInteiro, formatarReais } from '@/lib/formato';

import type { AbastecimentoDaApi } from './api';
import { litrosEscritos } from './litros';
import type { Abastecimento } from './tipos';

/**
 * QUEM LANÇOU E EM QUE MÁQUINA — o que a lista principal do Combustível
 * passou a mostrar (pedido do dono, 27/10/2026: "inclua o equipamento que foi
 * abastecido" e "ver quem lançou"). Regras que a mesa e o celular dividem.
 */

/**
 * A máquina por extenso, para a segunda linha do código:
 * "FORD CARGO 2628 E · Caminhão basculante".
 *
 * O tipo que a descrição já diz sai uma vez só: a frota de hoje descreve a
 * máquina como "CAMINHÃO BASCULANTE FMX 440", e acrescentar "· CAMINHÃO
 * BASCULANTE" repetia o tipo na mesma linha (prova pela tela, 27/10/2026). A
 * comparação ignora caixa e acento. `null` quando a máquina não tem nem um nem
 * outro: a célula escreve a ausência.
 */
export function maquinaPorExtenso(descricao: string | null | undefined, tipo: string | null | undefined): string | null {
  const d = descricao?.trim() ?? '';
  const t = tipo?.trim() ?? '';
  if (d === '' && t === '') return null;
  if (d === '') return t;
  if (t === '' || semAcentoNemCaixa(d).includes(semAcentoNemCaixa(t))) return d;
  return `${d} · ${t}`;
}

function semAcentoNemCaixa(texto: string): string {
  return texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('pt-BR');
}

/** O que a célula escreve quando a trilha não guarda quem gravou a linha. */
export const SEM_REGISTRO_DE_QUEM_LANCOU = 'Sem registro';

/**
 * QUEM LANÇOU — o nome de quem registrou, e nunca o operador nem o
 * responsável.
 *
 * O operador é quem estava na máquina; o responsável, quem liberou o diesel.
 * Os dois são escolhas de quem lança, numa lista. Completar o "lançado por"
 * com eles quando a trilha não diz atribuiria o registro a quem não
 * registrou — e é exatamente a pergunta que o dono quer responder.
 */
export function quemLancou(a: Pick<Abastecimento, 'registro'>): string {
  const nome = a.registro?.por?.trim() ?? '';
  return nome === '' ? SEM_REGISTRO_DE_QUEM_LANCOU : nome;
}

/** O mesmo, da linha crua da API (celular). */
export function quemLancouDaApi(a: Pick<AbastecimentoDaApi, 'registradoPor'>): string {
  const nome = a.registradoPor?.trim() ?? '';
  return nome === '' ? SEM_REGISTRO_DE_QUEM_LANCOU : nome;
}

export type CanalDoRegistro = 'folha' | 'celular' | 'mesa';

/**
 * Por onde o lançamento entrou. A folha de campo vence: ela é conferida na
 * mesa ou no celular, mas o número veio do papel, e é o papel que se procura
 * quando o número está errado.
 */
export function canalDoRegistro(a: { readonly daFolha?: boolean | undefined; readonly doCelular?: boolean | undefined }): CanalDoRegistro {
  if (a.daFolha === true) return 'folha';
  if (a.doCelular === true) return 'celular';
  return 'mesa';
}

export const TEXTO_DO_CANAL: Readonly<Record<CanalDoRegistro, string>> = {
  folha: 'Pela folha de campo',
  celular: 'Pelo celular',
  mesa: 'Pela mesa',
};

/* ======================================================================
 * A FICHA DO ABASTECIMENTO — o que o painel da lista principal mostra, e o
 * que a folha do celular mostra. Uma função só, para os dois dizerem a mesma
 * coisa: o painel da mesa e a folha do celular divergiam toda vez que um
 * campo novo entrava num lado e esquecia o outro.
 * ==================================================================== */

/** Um valor com unidade: o número em preto e a unidade em cinza (p.61). */
export interface MedidaDaFicha {
  readonly valor: string;
  readonly unidade: string;
  /** `true` para sufixo sem espaço, como o `/L` de `R$ 6,29/L`. */
  readonly colado?: boolean;
}

export interface ItemDaFicha {
  readonly rotulo: string;
  readonly valor: string | MedidaDaFicha;
  /** O valor é a AUSÊNCIA do dado ("Sem obra"): vai em cinza, não em preto. */
  readonly ausente?: boolean;
}

export interface FichaDoAbastecimento {
  /** O fato: máquina, produto, origem, volume, valor, medidor, destino. */
  readonly lancamento: readonly ItemDaFicha[];
  /** Quem lançou, quando, por onde, e quem estava na máquina e liberou. */
  readonly registro: readonly ItemDaFicha[];
}

const item = (rotulo: string, valor: string | null | undefined, ausencia: string): ItemDaFicha =>
  valor === null || valor === undefined || valor.trim() === '' ? { rotulo, valor: ausencia, ausente: true } : { rotulo, valor };

export function fichaDoAbastecimento(a: Abastecimento): FichaDoAbastecimento {
  const externo = a.tipo === 'externo' || (a.tipo === undefined && Boolean(a.posto));
  const total = a.valorTotal ?? (a.precoLitro === null ? null : a.litros * a.precoLitro);
  const odometro = a.tipoMedidor === 'odometro';
  const maquina = maquinaPorExtenso(a.equipamentoDescricao, a.equipamentoTipo);

  const lancamento: ItemDaFicha[] = [
    { rotulo: 'Máquina', valor: maquina === null ? a.equipamentoCodigo : `${a.equipamentoCodigo} · ${maquina}` },
    item('Produto', a.produto, 'Sem produto'),
    externo
      ? item('Origem', a.posto ? (/^posto\b/i.test(a.posto) ? a.posto : `Posto ${a.posto}`) : null, 'Posto não informado')
      : item('Origem', a.tanque, 'Tanque não informado'),
    ...(a.ordemNumero === null || a.ordemNumero === undefined ? [] : [{ rotulo: 'Ordem de abastecimento', valor: formatarInteiro(a.ordemNumero) }]),
    { rotulo: 'Litros', valor: { valor: litrosEscritos(a.litros), unidade: 'L' } },
    a.precoLitro === null
      ? { rotulo: 'Preço do litro', valor: externo ? 'Sem preço' : 'Sem preço: o tanque ainda não recebeu nota', ausente: true }
      : { rotulo: 'Preço do litro', valor: { valor: formatarReais(a.precoLitro), unidade: '/L', colado: true } },
    total === null ? { rotulo: 'Total', valor: 'Sem preço', ausente: true } : { rotulo: 'Total', valor: formatarReais(total) },
    a.horimetro === null
      ? { rotulo: odometro ? 'Odômetro' : 'Horímetro', valor: 'Sem leitura', ausente: true }
      : { rotulo: odometro ? 'Odômetro' : 'Horímetro', valor: { valor: formatarAteDuasCasas(a.horimetro), unidade: odometro ? 'km' : 'h' } },
    item('Obra', a.obra, 'Sem obra'),
    item('Centro de custo', a.centroCusto, 'Sem centro de custo'),
    ...(externo
      ? [
          item('Comprovante', a.comprovante, 'Sem comprovante'),
          /* A IMAGEM DO CUPOM NÃO FICA (0077): o que a leitura pela IA disse fica
             no registro dela, e o número do comprovante fica aqui. Escrever
             isso é melhor que um campo "Foto" vazio que faz procurar o que não
             existe. */
          { rotulo: 'Foto do cupom', valor: 'Não é guardada; fica o número do comprovante', ausente: true },
        ]
      : []),
    ...(a.ids?.observacao ? [{ rotulo: 'Observação', valor: a.ids.observacao }] : []),
  ];

  const registro: ItemDaFicha[] = [
    a.registro?.por ? { rotulo: 'Lançado por', valor: a.registro.por } : { rotulo: 'Lançado por', valor: 'A trilha não guarda quem gravou esta linha', ausente: true },
    ...(a.registro?.por ? [item('Perfil de quem lançou', a.registro.perfil, 'Sem papel em vigor')] : []),
    a.registro?.em ? { rotulo: 'Registrado em', valor: formatarDataHora(a.registro.em) } : { rotulo: 'Registrado em', valor: 'Não registrado', ausente: true },
    { rotulo: 'Ocorreu em', valor: formatarDataHora(a.quando) },
    { rotulo: 'Canal', valor: TEXTO_DO_CANAL[canalDoRegistro(a)] },
    item('Operador', a.operador, 'Sem operador'),
    item('Responsável', a.responsavel, 'Sem responsável'),
    a.conferencia
      ? { rotulo: 'Conferência', valor: `${a.conferencia.por ?? 'Alguém'} conferiu em ${formatarDataHora(a.conferencia.em)}` }
      : { rotulo: 'Conferência', valor: 'Ainda não conferido', ausente: true },
  ];

  return { lancamento, registro };
}

export type EstadoDoAbastecimento = 'vale' | 'estornado' | 'corrigido' | 'correcao';

export interface SituacaoDoAbastecimento {
  readonly estado: EstadoDoAbastecimento;
  /** O nome escrito do estado, para o selo (nunca só a cor). */
  readonly rotulo: string;
  readonly texto: string;
  /** O outro lado da correção, quando está na lista: o certo ou o estornado. */
  readonly relacionado: { readonly id: string; readonly rotulo: string } | null;
}

/**
 * A SITUAÇÃO da linha (ADR-008): vale, estornado, corrigido, ou a correção de
 * outro. O apagado não tem situação — a linha não existe mais (0126), e a
 * cópia dela mora no registro de exclusões.
 *
 * `porId` é a lista inteira, vigentes e estornados: é nela que se acha o outro
 * lado da correção. Fora dela (a lista é recortada nos mais recentes), o texto
 * diz o que se sabe e não oferece o atalho.
 */
export function situacaoDoAbastecimento(a: Abastecimento, porId: ReadonlyMap<string, Abastecimento>): SituacaoDoAbastecimento {
  const resumo = (x: Abastecimento) => `${formatarDataHora(x.quando)}, ${litrosEscritos(x.litros)} L`;
  const quemEstornou = (x: Abastecimento) =>
    x.estorno ? `${x.estorno.por ?? 'Alguém'} estornou em ${formatarDataHora(x.estorno.em)}` : 'Estornado';
  const motivo = (x: Abastecimento) =>
    x.estorno?.motivo ? ` Motivo: ${x.estorno.motivo.replace(/[.\s]+$/, '')}.` : ' Sem motivo registrado.';

  if (a.estorno) {
    if (a.corrigidoPorId) {
      const certo = porId.get(a.corrigidoPorId) ?? null;
      return {
        estado: 'corrigido',
        rotulo: 'Estornado',
        texto: `${quemEstornou(a)} e lançou o certo no lugar${certo ? ` (${resumo(certo)})` : ''}.${motivo(a)} Fica na lista anulado e não conta no consumo nem no custo.`,
        relacionado: certo ? { id: certo.id, rotulo: 'Ver o lançamento certo' } : null,
      };
    }
    return {
      estado: 'estornado',
      rotulo: 'Estornado',
      texto: `${quemEstornou(a)}.${motivo(a)} Fica na lista anulado e não conta no consumo nem no custo.`,
      relacionado: null,
    };
  }
  if (a.corrigeId) {
    const errado = porId.get(a.corrigeId) ?? null;
    return {
      estado: 'correcao',
      rotulo: 'Corrigido',
      texto: errado
        ? `Entrou no lugar do lançamento de ${resumo(errado)}. ${quemEstornou(errado)}.${motivo(errado)}`
        : 'Entrou no lugar de um lançamento estornado, que não está entre os carregados.',
      relacionado: errado ? { id: errado.id, rotulo: 'Ver o lançamento estornado' } : null,
    };
  }
  return {
    estado: 'vale',
    rotulo: 'Vale',
    texto: a.tipo === 'externo' ? 'Vale: conta no consumo, no custo e na cobrança do posto.' : 'Vale: conta no consumo, no custo e no saldo do tanque.',
    relacionado: null,
  };
}

/** A frase do cartão do celular: "Lançado por ANA SOUZA", ou a ausência dita. */
export function fraseDeQuemLancou(a: Pick<AbastecimentoDaApi, 'registradoPor'>): string {
  const nome = quemLancouDaApi(a);
  return nome === SEM_REGISTRO_DE_QUEM_LANCOU ? 'Sem registro de quem lançou' : `Lançado por ${nome}`;
}
